// This code has been partially generated by an AI.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStats, syncFromFile, syncVersions, syncGitHubOrg, listPlugins } from '../lib/api';
import type { Stats, SyncResult, SyncVersionsResult, OrgSyncResult, Plugin } from '../lib/api';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { TileSkeleton, TableSkeleton, EmptyState } from '../components/LoadingState';
import { CategoryDonut, StatusDonut } from '../components/StatCharts';

type TrendPoint = { period: string; views: number; downloads: number };

function buildZeroSeries(range: 'day' | 'week' | 'month'): TrendPoint[] {
  const now = new Date();
  const out: TrendPoint[] = [];
  const count = range === 'day' ? 14 : 12;

  for (let i = count - 1; i >= 0; i--) {
    const d = new Date(now);
    if (range === 'day') {
      d.setDate(d.getDate() - i);
      out.push({ period: d.toISOString().slice(0, 10), views: 0, downloads: 0 });
      continue;
    }
    if (range === 'week') {
      d.setDate(d.getDate() - i * 7);
      const weekStart = new Date(d);
      const day = (weekStart.getDay() + 6) % 7;
      weekStart.setDate(weekStart.getDate() - day);
      const year = weekStart.getUTCFullYear();
      const firstThursday = new Date(Date.UTC(year, 0, 4));
      const firstWeekStart = new Date(firstThursday);
      firstWeekStart.setUTCDate(firstThursday.getUTCDate() - ((firstThursday.getUTCDay() + 6) % 7));
      const diff = Math.round((weekStart.getTime() - firstWeekStart.getTime()) / (7 * 24 * 3600 * 1000));
      const week = Math.max(1, diff + 1);
      out.push({ period: `${year}-W${String(week).padStart(2, '0')}`, views: 0, downloads: 0 });
      continue;
    }
    d.setMonth(d.getMonth() - i);
    out.push({ period: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, views: 0, downloads: 0 });
  }

  return out;
}

export function linePath(values: number[], width: number, height: number, pad: number, max: number): string {
  if (values.length === 0) return '';
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  return values.map((v, i) => {
    const x = pad + (values.length === 1 ? innerW / 2 : (i / (values.length - 1)) * innerW);
    const y = pad + innerH - (v / max) * innerH;
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`;
  }).join(' ');
}

function SeriesLineChart({
  data,
  hoveredIndex,
  onHoverIndexChange,
}: Readonly<{
  data: TrendPoint[];
  hoveredIndex: number | null;
  onHoverIndexChange: (index: number | null) => void;
}>) {
  const width = 760;
  const height = 220;
  const pad = 18;
  // data is already oldest→newest (left→right) — no reversal needed
  const views = data.map((p) => p.views ?? 0);
  const downloads = data.map((p) => p.downloads ?? 0);
  const max = Math.max(1, ...views, ...downloads);
  const innerW = width - pad * 2;
  const innerH = height - pad * 2;
  const step = data.length <= 1 ? 0 : innerW / (data.length - 1);
  const pointX = (idx: number) => pad + (data.length === 1 ? innerW / 2 : idx * step);
  const pointY = (value: number) => pad + innerH - (value / max) * innerH;
  const viewPath = linePath(views, width, height, pad, max);
  const downloadPath = linePath(downloads, width, height, pad, max);
  const areaPath = (values: number[]) => {
    const line = linePath(values, width, height, pad, max);
    if (!line) return '';
    return `${line} L ${pointX(data.length - 1)} ${height - pad} L ${pad} ${height - pad} Z`;
  };
  const hoverX = hoveredIndex === null ? null : pointX(hoveredIndex);
  const hoverViewsY = hoveredIndex === null ? null : pointY(views[hoveredIndex] ?? 0);
  const hoverDownloadsY = hoveredIndex === null ? null : pointY(downloads[hoveredIndex] ?? 0);
  const hoveredPoint = hoveredIndex !== null ? data[hoveredIndex] : null;

  // Position tooltip box: flip to left side if near right edge
  const tooltipW = 160;
  const tooltipH = 56;
  const tooltipX = hoverX !== null
    ? (hoverX + tooltipW + 12 > width ? hoverX - tooltipW - 8 : hoverX + 8)
    : 0;
  const tooltipY = pad + 4;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="220"
      role="img"
      aria-label="Trend chart for views and downloads"
      onMouseLeave={() => onHoverIndexChange(null)}
    >
      {/* Every colour here is a theme token. The chart used to be painted in
          fixed dark values — a near-black plot area, a near-black tooltip and
          #c9d1d9 text — which became an unreadable dark rectangle the moment
          the palette flipped to light. */}
      <defs>
        <linearGradient id="viewGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--chart-views)" stopOpacity=".75" />
          <stop offset="100%" stopColor="var(--chart-views)" />
        </linearGradient>
        <linearGradient id="downloadGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--chart-downloads)" stopOpacity=".75" />
          <stop offset="100%" stopColor="var(--chart-downloads)" />
        </linearGradient>
        <linearGradient id="viewFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--chart-views)" stopOpacity=".28" />
          <stop offset="100%" stopColor="var(--chart-views)" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="downloadFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--chart-downloads)" stopOpacity=".22" />
          <stop offset="100%" stopColor="var(--chart-downloads)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width={width} height={height} rx="10" fill="var(--chart-surface)" stroke="var(--border)" strokeWidth="1" />
      {[0, .25, .5, .75, 1].map((fraction) => (
        <line key={fraction} x1={pad} x2={width - pad} y1={pad + innerH * fraction} y2={pad + innerH * fraction}
          stroke="var(--chart-grid)" strokeDasharray="2 5" />
      ))}
      <path d={areaPath(views)} fill="url(#viewFill)" />
      <path d={areaPath(downloads)} fill="url(#downloadFill)" />
      {/* Dashed versus solid, so the two series stay distinguishable without
          relying on colour alone. */}
      <path d={viewPath} fill="none" stroke="url(#viewGrad)" strokeWidth="2.5" strokeLinecap="round" />
      <path d={downloadPath} fill="none" stroke="url(#downloadGrad)" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="6 4" />
      {data.map((point, idx) => (
        <g key={`points-${point.period}`}>
          <circle cx={pointX(idx)} cy={pointY(views[idx] ?? 0)} r="2.5" fill="var(--chart-views)" />
          <circle cx={pointX(idx)} cy={pointY(downloads[idx] ?? 0)} r="2.5" fill="var(--chart-downloads)" />
        </g>
      ))}
      {hoverX !== null && hoveredPoint && (
        <>
          <line x1={hoverX} x2={hoverX} y1={pad} y2={height - pad} stroke="var(--chart-grid)" strokeDasharray="3 3" />
          <circle cx={hoverX} cy={hoverViewsY ?? 0} r="5" fill="var(--chart-views)" stroke="var(--chart-point-ring)" strokeWidth="1.5" />
          <circle cx={hoverX} cy={hoverDownloadsY ?? 0} r="5" fill="var(--chart-downloads)" stroke="var(--chart-point-ring)" strokeWidth="1.5" />
          {/* Tooltip box directly on the chart */}
          <rect x={tooltipX} y={tooltipY} width={tooltipW} height={tooltipH} rx="6" fill="var(--chart-tooltip-bg)" stroke="var(--border)" strokeWidth="1" />
          <text x={tooltipX + 8} y={tooltipY + 14} fontSize="10" fill="var(--text-muted)">{hoveredPoint.period}</text>
          <circle cx={tooltipX + 12} cy={tooltipY + 27} r="4" fill="var(--chart-views)" />
          <text x={tooltipX + 20} y={tooltipY + 31} fontSize="11" fill="var(--chart-tooltip-fg)">Views: <tspan fontWeight="bold">{Number(hoveredPoint.views ?? 0).toLocaleString()}</tspan></text>
          <circle cx={tooltipX + 12} cy={tooltipY + 44} r="4" fill="var(--chart-downloads)" />
          <text x={tooltipX + 20} y={tooltipY + 48} fontSize="11" fill="var(--chart-tooltip-fg)">Downloads: <tspan fontWeight="bold">{Number(hoveredPoint.downloads ?? 0).toLocaleString()}</tspan></text>
        </>
      )}
      {data.map((point, idx) => {
        const x = pointX(idx);
        const hit = Math.max(12, step || 24);
        return (
          <rect
            key={point.period}
            x={x - hit / 2}
            y={pad}
            width={hit}
            height={innerH}
            fill="transparent"
            tabIndex={0}
            aria-label={`${point.period}: ${Number(point.views ?? 0).toLocaleString()} views, ${Number(point.downloads ?? 0).toLocaleString()} downloads`}
            onMouseEnter={() => onHoverIndexChange(idx)}
            onFocus={() => onHoverIndexChange(idx)}
          />
        );
      })}
    </svg>
  );
}

export function TopPluginsBarChart({ data }: Readonly<{ data: NonNullable<Stats['topPlugins']> }>) {
  if (!data || data.length === 0) return null;
  const max = Math.max(1, ...data.map((d) => Math.max(d.views, d.downloads)));
  return (
    <div className="top-plugins-list">
      {data.slice(0, 6).map((item) => {
        const views = Number(item.views ?? 0);
        const downloads = Number(item.downloads ?? 0);
        const v = Math.max(2, Math.round((views / max) * 100));
        const d = Math.max(2, Math.round((downloads / max) * 100));
        return (
          <div key={item.pluginId} className="top-plugins-item">
            <div className="top-plugins-item__header">
              <span>{item.namespace ? `${item.namespace}/` : ''}{item.name}</span>
              <span className="muted">V {views.toLocaleString()} · D {downloads.toLocaleString()}</span>
            </div>
            {/* Keep the SVG viewport at the bar's actual width. Stretching a
                fixed viewBox would also stretch the rect's corner radius. */}
            <div className="top-plugins-bars">
              <svg width={`${v}%`} height="6" aria-hidden="true">
                <rect width="100%" height="6" rx="3" fill="var(--chart-views)" />
              </svg>
              <svg width={`${d}%`} height="6" aria-hidden="true">
                <rect width="100%" height="6" rx="3" fill="var(--chart-downloads)" />
              </svg>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const isAdmin = user?.isAdmin === true;
  const [stats, setStats]                     = useState<Stats | null>(null);
  const [error, setError]                     = useState('');
  const [syncing, setSyncing]                 = useState(false);
  const [syncingVersions, setSyncingVersions] = useState(false);
  const [syncingOrg, setSyncingOrg]           = useState(false);
  const [syncResult, setSyncResult]           = useState<SyncResult | null>(null);
  const [versionResult, setVersionResult]     = useState<SyncVersionsResult | null>(null);
  const [orgResult, setOrgResult]             = useState<OrgSyncResult | null>(null);
  const [pending, setPending]                 = useState<Plugin[]>([]);
  const [pendingLoading, setPendingLoading]   = useState(true);
  const [seriesRange, setSeriesRange]         = useState<'day' | 'week' | 'month'>('day');
  const [hoveredSeriesIndex, setHoveredSeriesIndex] = useState<number | null>(null);

  useEffect(() => {
    setHoveredSeriesIndex(null);
  }, [seriesRange, stats?.timestamp]);

  useEffect(() => {
    getStats().then(setStats).catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed'));
    if (isAdmin) loadPending();
  }, [isAdmin]);

  async function loadPending() {
    setPendingLoading(true);
    try {
      const r = await listPlugins({ status: 'pending', limit: 50 });
      setPending(r.data);
    } catch { setPending([]); }
    finally { setPendingLoading(false); }
  }

  async function handleSync() {
    setSyncing(true); setSyncResult(null); setError('');
    try {
      const r = await syncFromFile();
      setSyncResult(r);
      setStats(await getStats());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally { setSyncing(false); }
  }

  async function handleSyncVersions() {
    setSyncingVersions(true); setVersionResult(null); setError('');
    try {
      const r = await syncVersions();
      setVersionResult(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Version sync failed');
    } finally { setSyncingVersions(false); }
  }

  async function handleSyncOrg() {
    setSyncingOrg(true); setOrgResult(null); setError('');
    try {
      const r = await syncGitHubOrg();
      setOrgResult(r);
      setStats(await getStats());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Org sync failed');
    } finally { setSyncingOrg(false); }
  }

  const versionStats = versionResult
    ? versionResult.results.reduce(
        (acc, r) => ({ created: acc.created + r.created, skipped: acc.skipped + r.skipped, errors: acc.errors + (r.error ? 1 : 0) }),
        { created: 0, skipped: 0, errors: 0 },
      )
    : null;

  const orgStats = orgResult
    ? orgResult.results.reduce(
        (acc, r) => ({ created: acc.created + (r.action === 'created' ? 1 : 0), updated: acc.updated + (r.action === 'updated' ? 1 : 0), errors: acc.errors + (r.error ? 1 : 0) }),
        { created: 0, updated: 0, errors: 0 },
      )
    : null;

  const rawSeries = stats?.series?.[seriesRange] ?? [];
  const zeroSeries = buildZeroSeries(seriesRange);
  // Merge sparse API data into zero-filled baseline so the chart always has N points.
  // activeSeries is oldest→newest (left→right) — matches chart direction.
  const activeSeries = (() => {
    if (rawSeries.length === 0) return zeroSeries;
    const map = new Map(rawSeries.map((p) => [p.period, p]));
    return zeroSeries.map((z) => map.get(z.period) ?? z);
  })();
  const categories = stats?.categories ?? {};
  const totalPlugins = Number(stats?.totalPlugins ?? 0);
  const totalUsers = Number(stats?.totalUsers ?? 0);
  const totalViews = Number(stats?.totalViews ?? 0);
  const totalDownloads = Number(stats?.totalDownloads ?? 0);
  const topPlugins = stats?.topPlugins ?? [];
  const topVersions = stats?.topVersions ?? [];
  const statusCounts = stats?.statusCounts ?? {};
  const categoryData = Object.entries(categories)
    .map(([key, value]) => ({ key, label: key, value: Number(value ?? 0) }))
    .filter(entry => entry.value > 0);
  const hasStatusCounts = Object.values(statusCounts).some(value => Number(value ?? 0) > 0);
  // hoveredSeriesIndex maps directly to activeSeries (oldest→newest)
  const activePoint = (() => {
    if (activeSeries.length === 0) return null;
    if (hoveredSeriesIndex !== null && activeSeries[hoveredSeriesIndex]) return activeSeries[hoveredSeriesIndex];
    return activeSeries[activeSeries.length - 1] ?? null;
  })();

  return (
    <>
      <div className="page__header">
        <h1 className="page__title">Dashboard</h1>
        {isAdmin && (
          <div className="flex gap-sm flex-wrap">
            <button type="button" className="btn btn--secondary" onClick={() => { void handleSyncVersions(); }} disabled={syncingVersions}>
              {syncingVersions ? 'Syncing…' : '↓ Sync versions'}
            </button>
            <button type="button" className="btn btn--secondary" onClick={() => { void handleSyncOrg(); }} disabled={syncingOrg}>
              {syncingOrg ? 'Scanning…' : '⟳ Sync GitHub org'}
            </button>
            <button type="button" className="btn btn--primary" onClick={() => { void handleSync(); }} disabled={syncing}>
              {syncing ? 'Syncing…' : '⟳ Sync plugins.json'}
            </button>
          </div>
        )}
      </div>
      <div className="page__body">
        {error && <div className="alert alert--error">{error}</div>}
        {syncResult && (
          <div className="alert alert--success">
            plugins.json sync — created: {syncResult.created}, updated: {syncResult.updated}, failed: {syncResult.failed}
          </div>
        )}
        {versionStats && (
          <div className={versionStats.errors > 0 ? 'alert alert--error' : 'alert alert--success'}>
            Version sync — new: {versionStats.created}, up-to-date: {versionStats.skipped}
            {versionStats.errors > 0 && `, errors: ${versionStats.errors}`}
            {versionStats.errors > 0 && (
              <ul className="error-list">
                {versionResult!.results.filter(r => r.error).map(r => (
                  <li key={r.plugin}><code>{r.plugin}</code>: {r.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}
        {orgStats && (
          <div className={orgStats.errors > 0 ? 'alert alert--error' : 'alert alert--success'}>
            GitHub org sync — discovered: {orgResult!.total}, new: {orgStats.created}, updated: {orgStats.updated}
            {orgStats.errors > 0 && `, errors: ${orgStats.errors}`}
            {orgStats.errors > 0 && (
              <ul className="error-list">
                {orgResult!.results.filter(r => r.error).map(r => (
                  <li key={r.repo}><code>{r.repo}</code>: {r.error}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!stats && !error && <TileSkeleton label="Loading registry statistics" />}
        {stats && (
          <>
          <div className="dashboard-metrics">
            <div className="stat-card">
              <div className="stat-card__label">Plugins</div>
              <div className="stat-card__value">{totalPlugins}</div>
            </div>
            {isAdmin && (
              <div className="stat-card stat-card--admin">
                <div className="stat-card__label">Contributors</div>
                <div className="stat-card__value">{totalUsers.toLocaleString()}</div>
              </div>
            )}
            <div className="stat-card">
              <div className="stat-card__label">Views</div>
              <div className="stat-card__value">{totalViews.toLocaleString()}</div>
            </div>
            <div className="stat-card">
              <div className="stat-card__label">Downloads</div>
              <div className="stat-card__value">{totalDownloads.toLocaleString()}</div>
            </div>
          </div>

          {/* The four totals above stay numbers: a single current value has no
              shape, and a one-bar chart says less than the figure does. These
              two carry structure — a magnitude comparison and a part-to-whole
              split — which is what a chart is for. */}
          {(categoryData.length > 0 || hasStatusCounts) && (
            <div className="dashboard-panels">
              {/* A card is only drawn when its chart has something to draw —
                  an empty bordered box reads as a broken panel. */}
              {categoryData.length > 0 && (
                <div className="card">
                  <CategoryDonut data={categoryData} />
                </div>
              )}
              {isAdmin && hasStatusCounts && (
                <div className="card">
                  <StatusDonut counts={statusCounts} />
                </div>
              )}
            </div>
          )}

          <div className="card mt-2">
            <div className="flex justify-between items-center gap-md flex-wrap">
              <h2 className="m-0 text-md">Traffic trend</h2>
              <div className="flex gap-sm">
                {(['day', 'week', 'month'] as const).map((range) => (
                  <button
                    key={range}
                    type="button"
                    className={`btn btn--sm ${seriesRange === range ? 'btn--primary' : ''}`}
                    onClick={() => setSeriesRange(range)}
                  >
                    {range}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-md">
              <SeriesLineChart
                data={activeSeries}
                hoveredIndex={hoveredSeriesIndex}
                onHoverIndexChange={setHoveredSeriesIndex}
              />
              {/* The legend repeats the line style, not just the colour, so
                  the two series stay tellable apart without colour vision. */}
              <div className="chart-legend mt-1">
                <span className="chart-legend__item">
                  <svg width="18" height="8" aria-hidden="true"><line x1="0" y1="4" x2="18" y2="4" stroke="var(--chart-views)" strokeWidth="2.5" strokeLinecap="round" /></svg>
                  Views
                </span>
                <span className="chart-legend__item">
                  <svg width="18" height="8" aria-hidden="true"><line x1="0" y1="4" x2="18" y2="4" stroke="var(--chart-downloads)" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="6 4" /></svg>
                  Downloads
                </span>
              </div>
              {rawSeries.length === 0 && (
                <div className="chart-note mt-1">
                  Noch keine Events vorhanden. Die Grafik zeigt aktuell eine 0-Basislinie.
                </div>
              )}
              {activePoint && (
                <div className="chart-note mt-1">
                  <strong>{activePoint.period}</strong> · Views {Number(activePoint.views ?? 0).toLocaleString()} · Downloads {Number(activePoint.downloads ?? 0).toLocaleString()}
                </div>
              )}
            </div>
          </div>

          <div className="dashboard-panels">
            <div className="card">
              <h2 className="section-title">Top plugins</h2>
              {topPlugins.length === 0 ? (
                <p className="muted">No plugin metrics yet.</p>
              ) : (
                <>
                <TopPluginsBarChart data={topPlugins} />
                <div className="table-wrap mt-2">
                  <table className="table--stack">
                    <thead><tr><th>Plugin</th><th>Category</th><th>Views</th><th>Downloads</th></tr></thead>
                    <tbody>
                      {topPlugins.map((item) => (
                        <tr key={item.pluginId}>
                          <td data-label="Plugin">{item.namespace ? `${item.namespace}/` : ''}{item.name}</td>
                          <td data-label="Category" className="muted">{item.category}</td>
                          <td data-label="Views">{Number(item.views ?? 0).toLocaleString()}</td>
                          <td data-label="Downloads">{Number(item.downloads ?? 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </div>

            <div className="card">
              <h2 className="section-title">Top versions</h2>
              {topVersions.length === 0 ? (
                <p className="muted">No version metrics yet.</p>
              ) : (
                <div className="table-wrap">
                  <table className="table--stack">
                    <thead><tr><th>Version</th><th>Plugin</th><th>Views</th><th>Downloads</th></tr></thead>
                    <tbody>
                      {topVersions.map((item) => (
                        <tr key={item.versionId}>
                          <td data-label="Version"><code>v{item.version}</code></td>
                          <td data-label="Plugin">{item.namespace ? `${item.namespace}/` : ''}{item.pluginName}</td>
                          <td data-label="Views">{Number(item.views ?? 0).toLocaleString()}</td>
                          <td data-label="Downloads">{Number(item.downloads ?? 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          </>
        )}

        {/* Pending submissions — admin only */}
        {isAdmin && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-md m-0">
                Pending submissions
                {pending.length > 0 && (
                  <span className="count-badge">
                    {pending.length}
                  </span>
                )}
              </h2>
              <button
                className="btn btn--primary btn--header"
                onClick={() => navigate('/admin/submissions')}
              >
                Review submissions →
              </button>
            </div>
            {pendingLoading && <TableSkeleton label="Loading pending submissions" count={3} />}
            {!pendingLoading && pending.length === 0 && (
              <EmptyState title="Nothing waiting for review">
                Community submissions appear here. Approving one publishes it to
                the catalogue; rejecting one asks you for a reason the author
                will see.
              </EmptyState>
            )}
            {!pendingLoading && pending.length > 0 && pending.map(p => (
              <div key={p.id} className="pending-item">
                <div className="flex-1">
                  <a href={p.repository} target="_blank" rel="noreferrer" className="font-semibold">{p.name}</a>
                  <span className="muted text-xs ml-1">by {p.author}</span>
                  <div className="muted text-xs">{p.description}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
